/**
 * AssetManager — framework-level asset registry and loader.
 *
 * Scenes declare the assets they need via `register()`, then call
 * `preload(scene)` in their Phaser preload hook. The manager deduplicates
 * loads across scenes and tracks what's already been loaded.
 *
 * Stored in game.registry as "assets". Accessible from any scene.
 *
 * Usage:
 *   // In a scene or module — register assets once
 *   assets.register("image", "bg_clouds1", "/sprites/kenney-bg/clouds1.png");
 *   assets.register("audio", "hit_punch", "/audio/kenney-impact/impactPunch_heavy_000.ogg");
 *
 *   // In scene.preload() — queue registered assets for loading
 *   assets.preload(this);
 *
 *   // Or register + preload together for convenience
 *   assets.preload(this, [
 *     { type: "audio", key: "hit_punch", path: "/assets/audio/kenney-impact/Audio/impactPunch_heavy_000.ogg" },
 *   ]);
 */

declare const Phaser: any;

export type AssetType = "image" | "audio" | "spritesheet";

export interface AssetEntry {
  type: AssetType;
  key: string;
  path: string;
  /** Spritesheet frame config */
  frameWidth?: number;
  frameHeight?: number;
}

export class AssetManager {
  private game: any;
  /** All registered assets by key */
  private registry: Map<string, AssetEntry> = new Map();
  /** Keys that have been queued or loaded */
  private loaded: Set<string> = new Set();

  constructor(game: any) {
    this.game = game;
  }

  /** Register an asset for later loading. Idempotent — re-registering the same key is a no-op. */
  register(type: AssetType, key: string, path: string, opts?: { frameWidth?: number; frameHeight?: number }): void {
    if (this.registry.has(key)) return;
    this.registry.set(key, { type, key, path, ...opts });
  }

  /** Batch register multiple assets. */
  registerAll(entries: AssetEntry[]): void {
    for (const e of entries) {
      this.register(e.type, e.key, e.path, e);
    }
  }

  /**
   * Queue all registered (and not-yet-loaded) assets onto a scene's loader.
   * Call this in the scene's `preload()` method.
   *
   * Optionally pass extra entries to register + load in one step.
   */
  preload(scene: any, extras?: AssetEntry[]): void {
    if (extras) this.registerAll(extras);

    for (const [key, entry] of this.registry) {
      if (this.loaded.has(key)) continue;

      // Skip if Phaser already has this asset cached (e.g. from another scene)
      if (this.isAlreadyCached(scene, entry)) {
        this.loaded.add(key);
        continue;
      }

      this.queueLoad(scene, entry);
      this.loaded.add(key);
    }
  }

  /** Check if a key is registered. */
  has(key: string): boolean {
    return this.registry.has(key);
  }

  /** Check if a key has been loaded. */
  isLoaded(key: string): boolean {
    return this.loaded.has(key);
  }

  // --- Internals ---

  private isAlreadyCached(scene: any, entry: AssetEntry): boolean {
    switch (entry.type) {
      case "image":
      case "spritesheet":
        return scene.textures.exists(entry.key);
      case "audio":
        return scene.cache.audio.exists(entry.key);
      default:
        return false;
    }
  }

  private queueLoad(scene: any, entry: AssetEntry): void {
    switch (entry.type) {
      case "image":
        scene.load.image(entry.key, entry.path);
        break;
      case "audio":
        scene.load.audio(entry.key, entry.path);
        break;
      case "spritesheet":
        scene.load.spritesheet(entry.key, entry.path, {
          frameWidth: entry.frameWidth,
          frameHeight: entry.frameHeight,
        });
        break;
    }
  }
}
